import { useMemo, useState } from 'react';
import type { LeadNetworkEdge, LeadNetworkNode, LeadNetworkResponse } from '@/api/types';
import { rampStep } from './viz';

const WIDTH = 860;
const HEIGHT = 540;
const ITERATIONS = 150;

interface Placed { node: LeadNetworkNode; x: number; y: number; r: number }

/**
 * The lead similarity graph, laid out and drawn.
 *
 * <b>Colour encodes cluster RANK, not identity.</b> Communities arrive ordered by size, so the
 * sequential ramp already in the design system says something true — darker is bigger — and no
 * categorical palette has to be invented. Isolated leads are grey: "resembles nothing else here"
 * is a real state, not another cluster.
 *
 * <b>The layout is deterministic.</b> Positions are seeded from a hash of each lead's id, never
 * `Math.random`, so the same filter draws the same picture every time. A graph that rearranges
 * itself on each render cannot be discussed or screenshotted.
 */
export function LeadNetworkGraph({
  data, onSelect,
}: { data: LeadNetworkResponse; onSelect?: (id: string) => void }) {
  const [hovered, setHovered] = useState<string | null>(null);

  const { placed, byId } = useMemo(() => layout(data.nodes, data.edges), [data.nodes, data.edges]);
  const neighbours = useMemo(() => neighboursOf(hovered, data.edges), [hovered, data.edges]);

  if (data.nodes.length === 0) {
    return <div className="empty-state t-body">No leads in this range to compare.</div>;
  }

  const count = Math.max(data.communities.length, 1);
  const hoveredNode = hovered ? byId.get(hovered) : undefined;

  return (
    <div className="netgraph">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="netgraph-svg"
        role="img"
        aria-label={`Similarity graph of ${data.nodes.length} leads in ${data.communities.length} clusters`}
      >
        <g>
          {data.edges.map((edge) => {
            const a = byId.get(edge.source);
            const b = byId.get(edge.target);
            if (!a || !b) return null;
            const dim = hovered !== null && edge.source !== hovered && edge.target !== hovered;

            return (
              <line
                key={`${edge.source}-${edge.target}`}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                className={`netgraph-edge${dim ? ' netgraph-edge-dim' : ''}`}
                // Thicker = more attributes in common. Capped so one very similar pair cannot
                // draw a bar across the picture.
                strokeWidth={Math.min(1 + edge.weight / 4, 3)}
              />
            );
          })}
        </g>

        <g>
          {placed.map((p) => {
            const isHovered = hovered === p.node.id;
            const dim = hovered !== null && !isHovered && !neighbours.has(p.node.id);

            return (
              <circle
                key={p.node.id}
                cx={p.x} cy={p.y} r={isHovered ? p.r + 2 : p.r}
                fill={fillFor(p.node.community, count)}
                className={[
                  'netgraph-node',
                  p.node.is_bridge ? 'netgraph-node-bridge' : '',
                  dim ? 'netgraph-node-dim' : '',
                  onSelect ? 'netgraph-node-clickable' : '',
                ].filter(Boolean).join(' ')}
                onMouseEnter={() => setHovered(p.node.id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onSelect?.(p.node.id)}
              >
                <title>
                  {[
                    p.node.label,
                    [p.node.status, p.node.country, p.node.campaign].filter(Boolean).join(' · '),
                    p.node.community >= 0 ? `Cluster ${p.node.community + 1}` : 'Unclustered',
                    p.node.is_bridge ? 'Bridges two clusters' : '',
                  ].filter(Boolean).join('\n')}
                </title>
              </circle>
            );
          })}
        </g>
      </svg>

      <div className="netgraph-footer">
        <div className="netgraph-legend">
          {data.communities.slice(0, 8).map((c) => (
            <span key={c.id} className="netgraph-legend-item t-caption">
              <span className="netgraph-swatch" style={{ background: fillFor(c.id, count) }} aria-hidden="true" />
              Cluster {c.id + 1} · {c.size}
            </span>
          ))}
          {data.isolated_count > 0 && (
            <span className="netgraph-legend-item t-caption">
              <span className="netgraph-swatch" style={{ background: 'var(--viz-deemphasis)' }} aria-hidden="true" />
              Unclustered · {data.isolated_count}
            </span>
          )}
          <span className="netgraph-legend-item t-caption">
            <span className="netgraph-swatch netgraph-swatch-bridge" aria-hidden="true" />
            Bridge
          </span>
        </div>

        <div className="t-caption subtle">
          {hoveredNode
            ? `${hoveredNode.node.label} — ${neighbours.size} similar lead${neighbours.size === 1 ? '' : 's'}`
            : 'Hover a lead to isolate its ties. Click to open its profile.'}
        </div>
      </div>
    </div>
  );
}

/** Cluster rank → ramp step. -1 (isolated) is grey, deliberately outside the ramp. */
function fillFor(community: number, count: number): string {
  return community < 0
    ? 'var(--viz-deemphasis)'
    : rampStep(Math.min(community, count - 1), Math.max(count, 2));
}

function neighboursOf(id: string | null, edges: LeadNetworkEdge[]): Set<string> {
  const set = new Set<string>();
  if (id === null) return set;
  for (const edge of edges) {
    if (edge.source === id) set.add(edge.target);
    else if (edge.target === id) set.add(edge.source);
  }
  return set;
}

/**
 * Fruchterman-Reingold: pairwise repulsion, attraction along edges, a pull towards each cluster's
 * centroid, and gravity to the middle.
 *
 * Run to completion inside a memo rather than animated — the intermediate frames of a physics
 * simulation are not information, and a settling graph is harder to read than a settled one.
 */
function layout(nodes: LeadNetworkNode[], edges: LeadNetworkEdge[]) {
  const n = nodes.length;
  const byId = new Map<string, Placed>();
  if (n === 0) return { placed: [] as Placed[], byId };

  const index = new Map<string, number>();
  nodes.forEach((node, i) => index.set(node.id, i));

  const x = new Float64Array(n);
  const y = new Float64Array(n);
  const dx = new Float64Array(n);
  const dy = new Float64Array(n);

  // Deterministic start: golden-angle spiral, offset per node by a hash of its id.
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const jitter = (hash(nodes[i]!.id) % 1000) / 1000;
    const radius = (Math.sqrt((i + 0.5) / n) * Math.min(WIDTH, HEIGHT)) / 2.4;
    const angle = i * golden + jitter * 0.6;
    x[i] = WIDTH / 2 + radius * Math.cos(angle);
    y[i] = HEIGHT / 2 + radius * Math.sin(angle);
  }

  const links: { a: number; b: number; w: number }[] = [];
  for (const edge of edges) {
    const a = index.get(edge.source);
    const b = index.get(edge.target);
    if (a !== undefined && b !== undefined) links.push({ a, b, w: edge.weight });
  }

  const k = Math.sqrt((WIDTH * HEIGHT) / n) * 0.55;
  let temperature = Math.min(WIDTH, HEIGHT) / 8;
  const cooling = temperature / (ITERATIONS + 1);

  const cxs = new Float64Array(64);
  const cys = new Float64Array(64);
  const sizes = new Float64Array(64);

  for (let iteration = 0; iteration < ITERATIONS; iteration++) {
    dx.fill(0);
    dy.fill(0);

    // Repulsion — the O(n²) half, bounded by the server's node cap.
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let ex = x[i]! - x[j]!;
        let ey = y[i]! - y[j]!;
        let d2 = ex * ex + ey * ey;

        if (d2 < 0.01) {
          // Perfect overlap has no direction to separate in; nudge by index, not at random.
          ex = ((i % 7) - 3) * 0.1;
          ey = ((j % 7) - 3) * 0.1;
          d2 = ex * ex + ey * ey + 0.01;
        }

        const d = Math.sqrt(d2);
        const f = (k * k) / d;
        const fx = (ex / d) * f;
        const fy = (ey / d) * f;

        // Written out rather than `+=`: under noUncheckedIndexedAccess a compound assignment
        // reads the element first, and that read is `number | undefined`.
        dx[i] = dx[i]! + fx; dy[i] = dy[i]! + fy;
        dx[j] = dx[j]! - fx; dy[j] = dy[j]! - fy;
      }
    }

    // Attraction, scaled by similarity: leads with more in common settle closer together.
    for (const link of links) {
      const ex = x[link.a]! - x[link.b]!;
      const ey = y[link.a]! - y[link.b]!;
      const d = Math.max(Math.sqrt(ex * ex + ey * ey), 0.01);
      const f = ((d * d) / k) * Math.min(link.w / 3, 2);
      const fx = (ex / d) * f;
      const fy = (ey / d) * f;

      dx[link.a] = dx[link.a]! - fx; dy[link.a] = dy[link.a]! - fy;
      dx[link.b] = dx[link.b]! + fx; dy[link.b] = dy[link.b]! + fy;
    }

    // Cluster cohesion — without it communities interleave and the colours stop meaning
    // anything spatially.
    cxs.fill(0); cys.fill(0); sizes.fill(0);
    for (let i = 0; i < n; i++) {
      const c = nodes[i]!.community;
      if (c < 0 || c >= sizes.length) continue;
      cxs[c] = cxs[c]! + x[i]!;
      cys[c] = cys[c]! + y[i]!;
      sizes[c] = sizes[c]! + 1;
    }
    for (let i = 0; i < n; i++) {
      const c = nodes[i]!.community;
      if (c < 0 || c >= sizes.length || sizes[c]! === 0) continue;
      dx[i] = dx[i]! + (cxs[c]! / sizes[c]! - x[i]!) * 0.08;
      dy[i] = dy[i]! + (cys[c]! / sizes[c]! - y[i]!) * 0.08;
    }

    // Gravity keeps disconnected leads from drifting off the canvas.
    for (let i = 0; i < n; i++) {
      dx[i] = dx[i]! + (WIDTH / 2 - x[i]!) * 0.012;
      dy[i] = dy[i]! + (HEIGHT / 2 - y[i]!) * 0.012;

      const disp = Math.sqrt(dx[i]! * dx[i]! + dy[i]! * dy[i]!);
      if (disp > 0.0001) {
        const limited = Math.min(disp, temperature);
        x[i] = x[i]! + (dx[i]! / disp) * limited;
        y[i] = y[i]! + (dy[i]! / disp) * limited;
      }
    }

    temperature -= cooling;
  }

  // Fit to the viewport so a sparse graph fills the frame like a dense one.
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < n; i++) {
    minX = Math.min(minX, x[i]!); maxX = Math.max(maxX, x[i]!);
    minY = Math.min(minY, y[i]!); maxY = Math.max(maxY, y[i]!);
  }

  const margin = 28;
  const spanX = Math.max(maxX - minX, 1);
  const spanY = Math.max(maxY - minY, 1);
  const scale = Math.min((WIDTH - margin * 2) / spanX, (HEIGHT - margin * 2) / spanY);
  const maxDegree = Math.max(...nodes.map((node) => node.degree), 1);

  const placed = nodes.map((node, i) => {
    const p: Placed = {
      node,
      x: margin + (x[i]! - minX) * scale + (WIDTH - margin * 2 - spanX * scale) / 2,
      y: margin + (y[i]! - minY) * scale + (HEIGHT - margin * 2 - spanY * scale) / 2,
      // Area, not radius, tracks connectedness — radius-linear exaggerates the hubs.
      r: 4 + Math.sqrt(node.degree / maxDegree) * 7,
    };
    byId.set(node.id, p);
    return p;
  });

  return { placed, byId };
}

/** FNV-1a — small, stable, dependency-free. Only used to seed positions. */
function hash(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}
